// getMessageThumbnail.js
'use strict';
import createCachedSelector from '../resource/createCachedSelector.js';

import { PUBLIC_URL_PREFIX } from '../RemoteConfigKeys.js';
import getIsPreviewClear from '../selector/getIsPreviewClear.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getMeData from '../selector/getMeData.js';
import { getPoster, MediaPreset } from '../resource/getMediaAsset.js';
import getMessageData from '../selector/getMessageData.js';

const isServer = typeof window === 'undefined';

/**
 * Select message thumbnail.
 * Prefers `metadata.thumbnail_url` / `metadata.thumbnail_blurred_url`,
 * and falls back to the constructed poster.jpg url when absent.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} messageId - message id.
 * @param {string} assetId - asset id.
 * @param {string} [size = '128x128'] - message thumbnail size.
 * @param {string} [extension = 'jpg'] - message thumbnail extension.
 * @param {string} [preset] - message thumbnail preset. ex: 'clear', 'blurred'
 * @param {boolean} [shouldUseToken = false] - should set token on thumbnail url.
 * @return {string} Return message thumbnail url.
 */
const getMessageThumbnail = createCachedSelector(
  state => getMeData(state, 'token'),
  state => (getIsPreviewClear(state) ? 'clear' : 'blurred'),
  (state, messageId) => messageId,
  (state, messageId, assetId) => assetId,
  (state, messageId, assetId, size) => size,
  (state, messageId, assetId, size, extension) => extension,
  (state, messageId, assetId, size, extension, preset) => preset,
  (state, messageId, assetId, size, extension, preset, shouldUseToken) =>
    shouldUseToken,
  (state, messageId) => getMessageData(state, messageId, 'canViewMedia'),
  state => getRemoteConfigData(state, PUBLIC_URL_PREFIX),
  (state, messageId) => getMessageData(state, messageId, 'senderId'),
  state => getMeData(state, 'id'),
  (state, messageId) => getMessageData(state, messageId, 'thumbnailUrl'),
  (state, messageId) => getMessageData(state, messageId, 'thumbnailBlurredUrl'),
  (
    token,
    trailerPreset,
    messageId,
    assetId,
    size = '128x128',
    extension = 'jpg',
    preset,
    shouldUseToken = false,
    canViewMedia = false,
    // Use `PUBLIC_URL_PREFIX` as result function parameter to generate correct poster url on SSR.
    // This solve the issue in SSR where different users with different `PUBLIC_URL_PREFIX` try to generate a poster url, but they all end up using the poster URL directly from the cache.
    // eslint-disable-next-line no-unused-vars
    publicUrlPrefix,
    senderId,
    meId,
    thumbnailUrl,
    thumbnailBlurredUrl
  ) => {
    const usingPreset = preset
      ? preset
      : canViewMedia
        ? 'clear'
        : trailerPreset;

    // always show clear preset on the server,
    // and follow the remote config on the client
    const shouldUseClearPreset = isServer || usingPreset === 'clear';
    const url = getPoster({
      id: messageId,
      assetId,
      userId: senderId,
      meId,
      size,
      format: extension,
      preset: shouldUseClearPreset ? MediaPreset.SD : MediaPreset.SD_PREVIEW,
      thumbnailUrl: shouldUseClearPreset ? thumbnailUrl : thumbnailBlurredUrl,
    });
    if (shouldUseToken && token) {
      url.searchParams.set('auth', token);
    }
    return url.href;
  }
)(
  (
    state,
    messageId,
    assetId,
    size = '128x128',
    extension = 'jpg',
    preset,
    shouldUseToken = false
  ) =>
    `${messageId}:${assetId}:${size}:${extension}:${preset}:${shouldUseToken}`
);

export default getMessageThumbnail;
